import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DayField } from "@/components/DayField";
import { api, apiError } from "@/lib/api";

const EMPTY = {
  id: null,
  kind: "",
  title: "",
  units: [],
  date: "",
  time: "11:00",
  mode: "Online",
  venue: "",
  chaired_by: "",
  agenda: "",
  minutes: "",
  invitees: [],
  reminder_days: [2, 0],
  status: "scheduled",
};

const LEAD_LABEL = { 0: "Morning of it", 1: "1 day before", 2: "2 days before",
                     3: "3 days before", 7: "A week before" };

const Field = ({ label, hint, wide, children }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <label className="label-caps">{label}</label>
    <div className="mt-1">{children}</div>
    {hint && <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

/** Scheduling a review meeting — and deciding how long before it the units are told. */
export const MeetingFormDialog = ({ open, onOpenChange, meeting, kinds = [], kindHelp = {},
                                    modes = [], plants = [], leads = [], onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, kind: kinds[0] || "", ...(meeting || {}) });
  }, [open, meeting, kinds]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleUnit = (code) =>
    set("units", form.units.includes(code)
      ? form.units.filter((c) => c !== code)
      : [...form.units, code]);
  const toggleLead = (day) =>
    set("reminder_days", form.reminder_days.includes(day)
      ? form.reminder_days.filter((d) => d !== day)
      : [...form.reminder_days, day]);

  const save = async () => {
    if (!form.kind || !form.date) {
      toast.error("The meeting needs a kind and a date.");
      return;
    }
    setBusy(true);
    const body = {
      kind: form.kind,
      title: form.title || "",
      units: form.units,
      date: form.date,
      time: form.time || "",
      mode: form.mode,
      venue: form.venue || "",
      chaired_by: form.chaired_by || "",
      agenda: form.agenda || "",
      minutes: form.minutes || "",
      invitees: Array.isArray(form.invitees)
        ? form.invitees
        : String(form.invitees || "")
            .split(/[,\s;]+/)
            .filter(Boolean),
      reminder_days: form.reminder_days,
      status: form.status || "scheduled",
    };
    try {
      if (form.id) await api.patch(`/meetings/${form.id}`, body);
      else await api.post("/meetings", body);
      toast.success(
        form.id ? "The meeting has been corrected." : `${body.kind} is on the cadence.`,
      );
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[88vh] overflow-y-auto rounded-none bg-card sm:max-w-2xl"
        data-testid="meeting-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {form.id ? "Correct this meeting" : "Schedule a meeting"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Everybody who must be there is told on the portal's own bell, and by mail, on the lead
            times you pick below. A reminder already sent is never sent twice.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Which review *" hint={kindHelp[form.kind] || ""}>
            <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
              <SelectTrigger className="h-11 rounded-none" data-testid="mt-kind">
                <SelectValue placeholder="Pick the review" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {kinds.map((k) => (
                  <SelectItem key={k} value={k}>
                    {k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Subject" hint="Left blank, the review's own name is used.">
            <Input
              className="h-11 rounded-none"
              data-testid="mt-title"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
            />
          </Field>
          <Field label="Date *">
            <DayField
              tall
              testId="mt-date"
              format="iso"
              value={form.date}
              onChange={(v) => set("date", v)}
            />
          </Field>
          <Field label="Time (IST)">
            <Input
              className="font-data h-11 rounded-none"
              data-testid="mt-time"
              placeholder="11:00"
              value={form.time}
              onChange={(e) => set("time", e.target.value)}
            />
          </Field>
          <Field label="How">
            <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger className="h-11 rounded-none" data-testid="mt-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {modes.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Where / the link">
            <Input
              className="h-11 rounded-none"
              data-testid="mt-venue"
              placeholder="SDTD conference room, Ranchi"
              value={form.venue}
              onChange={(e) => set("venue", e.target.value)}
            />
          </Field>
          <Field label="Chaired by">
            <Input
              className="h-11 rounded-none"
              data-testid="mt-chair"
              value={form.chaired_by}
              onChange={(e) => set("chaired_by", e.target.value)}
            />
          </Field>
          {!!form.id && (
            <Field label="Where it stands">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-11 rounded-none" data-testid="mt-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-popover">
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="held">Held</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          <Field
            label="Units it covers"
            wide
            hint="Pick none and it is a SAIL-wide meeting — every unit's SPOC is told."
          >
            <div className="flex flex-wrap gap-2" data-testid="mt-units">
              <Button
                type="button"
                variant={form.units.length ? "outline" : "default"}
                className="h-9 rounded-none font-display text-[10px] font-bold uppercase tracking-widest"
                data-testid="mt-units-all"
                onClick={() => set("units", [])}
              >
                All of SAIL
              </Button>
              {plants.map((p) => (
                <Button
                  key={p.code}
                  type="button"
                  variant={form.units.includes(p.code) ? "default" : "outline"}
                  className="h-9 rounded-none font-display text-[10px] font-bold uppercase tracking-widest"
                  data-testid={`mt-units-${p.code}`}
                  onClick={() => toggleUnit(p.code)}
                >
                  {p.code}
                </Button>
              ))}
            </div>
          </Field>

          <Field
            label="Remind them"
            wide
            hint="Every unit SPOC the meeting covers, plus anyone named below."
          >
            <div className="flex flex-wrap gap-2" data-testid="mt-leads">
              {(leads.length ? leads : [7, 3, 2, 1, 0]).map((day) => (
                <Button
                  key={day}
                  type="button"
                  variant={form.reminder_days.includes(day) ? "default" : "outline"}
                  className="h-9 rounded-none font-display text-[10px] font-bold uppercase tracking-widest"
                  data-testid={`mt-lead-${day}`}
                  onClick={() => toggleLead(day)}
                >
                  {LEAD_LABEL[day] || `${day} days before`}
                </Button>
              ))}
            </div>
          </Field>

          <Field
            label="Anybody else to be told"
            wide
            hint="E-mail addresses, separated by commas."
          >
            <Input
              className="font-data h-11 rounded-none"
              data-testid="mt-invitees"
              placeholder="officer@sail.in, another@sail.in"
              value={Array.isArray(form.invitees) ? form.invitees.join(", ") : form.invitees}
              onChange={(e) =>
                set(
                  "invitees",
                  e.target.value
                    .split(/[,\s;]+/)
                    .map((x) => x.trim())
                    .filter(Boolean),
                )
              }
            />
          </Field>

          <Field label="Agenda" wide>
            <Textarea
              rows={3}
              className="rounded-none"
              data-testid="mt-agenda"
              placeholder="Unit-wise position, benefit vetted, what SDTD needs from each unit"
              value={form.agenda}
              onChange={(e) => set("agenda", e.target.value)}
            />
          </Field>
          {!!form.id && (
            <Field label="What came out of it" wide hint="Fill this in once the meeting is held.">
              <Textarea
                rows={3}
                className="rounded-none"
                data-testid="mt-minutes"
                value={form.minutes}
                onChange={(e) => set("minutes", e.target.value)}
              />
            </Field>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="h-11 rounded-none"
            onClick={() => onOpenChange(false)}
          >
            Leave it
          </Button>
          <Button
            className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            data-testid="mt-save"
            disabled={busy}
            onClick={save}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {form.id ? "Save the correction" : "Put it on the cadence"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
